import React, { useMemo, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from '@web3-react/core'
import styled from 'styled-components'
import { Flex, Text, Button, Skeleton, useModal } from 'uikit'
import PnlChart from './History/PnlTab/PnlChart'
import { getPnlSummary } from './History/PnlTab/PnlTab'
import { useGetCurrentEpoch, useGetHistory, useGetIsFetchingHistory } from 'state/predictions/hooks'
import { getFilteredBets } from 'state/predictions/helpers'
import { HistoryFilter } from 'state/types'
import { fetchNodeHistory } from 'state/predictions'
import { useConfig } from '../context/ConfigProvider'
import useLocalDispatch from '../../../contexts/LocalRedux/useLocalDispatch'
import useSelector from 'contexts/LocalRedux/useSelector'
import { multiplyPriceByAmount } from 'utils/prices'
import { formatMjt } from './History/helpers'
import { usePriceMjtUSDT } from 'state/hooks'
import CollectRoundWinningsModal from './CollectRoundWinningsModal'
import { useCollectWinningModalProps } from 'state/predictions/hooks'
import { useResponsive } from 'hooks/useResponse'
import { useReferralCode } from 'state/predictions/hooks'

import { shortenAddress } from 'utils'

const CardBg = styled(Flex)<{ hasClaimable: boolean }>`
  width: ${({ hasClaimable }) => (hasClaimable ? '480px' : '286px')};
  height: 132px;
  border-radius: 20px;
  background: white;
  padding: 16px 20px;
  align-items: center;
  @media (max-width: 768px) {
    width: 100%;
    height: 156px;
  }
`

const ImgCup = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`

const PredictionProfile: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const dispatch = useLocalDispatch()
  const { isMobile } = useResponsive()
  const { history, isLoadingHistory } = useCollectWinningModalProps()
  const bets = useGetHistory()
  const currentEpoch = useGetCurrentEpoch()
  const isFetchingHistory = useGetIsFetchingHistory()
  const { payToken, address: predictionsAddress, token } = useConfig()
  const results = getFilteredBets(bets, HistoryFilter.ALL)
  const summary = getPnlSummary(results, currentEpoch)
  const netResultAmount = summary.won.payout - summary.lost.amount
  const netResultIsZero = netResultAmount === 0
  const netResultIsPositive = netResultAmount > 0
  const mjtPrice = usePriceMjtUSDT()
  const netResultInUsd = multiplyPriceByAmount(mjtPrice, Math.abs(netResultAmount))
  const claimableStatus = useSelector((state) => state.claimableStatuses)
  const [isDone, setIsDone] = useState(false)
  const hasClaimable = useMemo(() => {
    const list = Object.values(claimableStatus)
    return list.includes(true)
  }, [claimableStatus])

  const referralCode = useReferralCode()
  const code = useMemo(() => {
    return referralCode
  }, [referralCode])

  useEffect(() => {
    if (hasClaimable && isDone) {
      setIsDone(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasClaimable])

  const [onPresentCollectWinningsModal] = useModal(
    <CollectRoundWinningsModal
      dispatch={dispatch}
      history={history}
      isLoadingHistory={isLoadingHistory}
      onSuccess={async () => {
        console.log('success')
        setIsDone(true)
      }}
      predictionsAddress={predictionsAddress}
      token={token}
      code={code}
    />,
    false,
    true,
    'CollectRoundWinningsModal'
  )

  React.useEffect(() => {
    if (account) {
      dispatch(fetchNodeHistory({ account }))
    }
  }, [account, dispatch])

  return (
    <div>
      {/* @ts-ignore */}
      <Helmet title="MojitoSwap Prediction  -  fun and simple decentralized prediction market">
        <meta
          name="description"
          content="MojitoSwap Prediction is a fun and simple decentralized prediction market.It gives you a chance to win huge MJT prizes only in 5minutes!"
        />
        <meta name="keywords" content="MojitoSwap, Dex, Games, MJT, Prediction" />
        <meta
          name="twitter:description"
          content="MojitoSwap Prediction is a fun and simple decentralized prediction market.It gives you a chance to win huge MJT prizes only in 5minutes!"
        />
        <meta property="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MojitoSwap Prediction  -  fun and simple decentralized prediction market" />
      </Helmet>
      <Text bold mb="10px" mt="15px">
        Hi, {shortenAddress(account)}
      </Text>
      {/* <Text bold mb="10px" mt="15px">
        Hi, {shortenAddress(account)}
      </Text> */}
      <CardBg hasClaimable={hasClaimable}>
        <Flex alignItems="center" style={{ width: '100%' }}>
          {isFetchingHistory ? (
            <Skeleton height="116px" width="116px" minWidth="116px" />
          ) : (
            <PnlChart won={results.length ? summary.won.rounds : 0} lost={results.length ? summary.lost.rounds : 0} />
          )}
          <Flex justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
            <Flex flexDirection="column" justifyContent="center" pl="17px">
              <Text fontSize="14px">{t('PREDICTION_25')}</Text>
              <Text
                bold
                fontSize="18px"
                lineHeight="1"
                color={netResultIsZero ? 'textRemark' : netResultIsPositive ? 'primary' : '#F9479C'}
                mt="8px"
                mb="8px"
              >
                {`${netResultIsPositive ? '+' : ''}${formatMjt(netResultAmount)} ${payToken.symbol}`}
              </Text>
              <Text fontSize="14px" color="textRemark">
                {`~${netResultIsPositive ? '+' : ''}$${netResultInUsd.toFixed(2)}`}
              </Text>
              {isMobile && hasClaimable ? (
                <Button
                  height="32px"
                  mt="12px"
                  style={{ fontSize: '14px', padding: '0 15px' }}
                  disabled={!hasClaimable || isLoadingHistory || isDone}
                  onClick={() => {
                    if (hasClaimable || !isLoadingHistory) {
                      onPresentCollectWinningsModal()
                    }
                  }}
                >
                  <ImgCup src={require('assets/images/prediction/cup.svg').default} />
                  {t('PREDICTION_26')}
                </Button>
              ) : null}
            </Flex>
            {isMobile ? null : hasClaimable ? (
              <Button
                height="32px"
                style={{ fontSize: '14px' }}
                disabled={!hasClaimable || isLoadingHistory || isDone}
                onClick={() => {
                  if (hasClaimable || !isLoadingHistory) {
                    onPresentCollectWinningsModal()
                  }
                }}
              >
                <ImgCup src={require('assets/images/prediction/cup.svg').default} />
                {t('PREDICTION_26')}
              </Button>
            ) : null}
          </Flex>
        </Flex>
      </CardBg>
    </div>
  )
}

export default PredictionProfile
